/**
 * Financial Analysis View — renders one of the two Investigations
 * destinations backed by the financial-analysis task (MOR-1280):
 *   - historical: Worksheets + Agent feed
 *   - solvency:   Indicators (landing) + Agent feed
 * The route's context (granularity, evidence period, open indicator) is
 * carried across the cross-links so neither destination loses the
 * practitioner's place. Manages document viewing state for source document
 * drill-in.
 */

import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Doc, Id } from "@cvx/_generated/dataModel";
import { Button } from "@/ui/primitives/button";
import { Badge } from "@/ui/primitives/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/ui/composed/sections/tabs";
import { AiDisclaimer } from "@/ui/composed/ai-disclaimer";
import {
  ArrowLeft,
  RotateCcw,
  RefreshCw,
  Table2,
  AlertTriangle,
  Bot,
  MessageSquare,
  ArrowRight,
} from "lucide-react";
import { formatDuration } from "../shared";
import { StatusDot } from "../StatusBadges";
import { getTaskTree, UnifiedFeed } from "../shared-panels";
import { SummaryChatSheet } from "../SummaryChatSheet";
import { ExportButton } from "@/ui/composed/ExportButton";
import { DocumentDetail } from "../../documents/DocumentDetail";
import type { FinancialAnalysisViewProps } from "./types";
import { FinancialWorksheetPanel } from "./FinancialWorksheetPanel";
import { FinancialIndicatorsPanel } from "./FinancialIndicatorsPanel";
import { buildFinancialAnalysisExport } from "./buildFinancialAnalysisExport";
import {
  FINANCIAL_SECTION_TITLES,
  defaultTabForSection,
  tabsForSection,
  titleForSection,
} from "./financialSections";

/**
 * Detail view for a financial-analysis task, rendered as either the
 * Historical Financial Analysis or Solvency Analysis section (MOR-1280).
 * Tabs, header, and cross-section links depend on `section`; the selected
 * granularity, period, and indicator live in the route-carried `context`.
 */
export function FinancialAnalysisView({
  taskId,
  jobId,
  onBack,
  onRerun,
  isViewer,
  section = "solvency",
  context = {},
  onContextChange,
  onOpenSection,
}: FinancialAnalysisViewProps) {
  const [chatOpen, setChatOpen] = useState(false);
  const [viewingDocumentId, setViewingDocumentId] =
    useState<Id<"documents"> | null>(null);

  const reextract = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.financialAnalysis.reextractApi
        .reextractFinancials,
    ),
  });

  async function handleReextract() {
    if (
      !window.confirm(
        "Re-extract all financial documents for this job? This re-runs extraction (incurs cost) and rebuilds the period columns.",
      )
    ) {
      return;
    }
    await reextract.mutateAsync({ jobId });
  }

  const { data: task } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.getSummarizationTaskDetail,
      { taskId },
    ),
  );

  // Period worksheets + indicators feed the rich multi-sheet export (the
  // summary counts in task.output are only a fraction of the on-screen data).
  // The export renders on whichever basis the practitioner has active, and
  // the title states it (MOR-810).
  const { data: exportSettings } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.getFinancialAnalysisSettings,
      { jobId },
    ),
  );
  const exportBasis = exportSettings?.analysisBasis ?? "original";
  const { data: periodEntries } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.listFinancialPeriodEntriesForJob,
      { jobId, basis: exportBasis },
    ),
  );
  const { data: indicatorEntries } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.listFinancialIndicatorEntriesForJob,
      { jobId },
    ),
  );
  // The query still declares `returns: v.array(v.any())`; its handler returns
  // financialIndicatorEntries docs verbatim, typed here at the boundary.
  const indicators: Doc<"financialIndicatorEntries">[] = indicatorEntries ?? [];

  if (!task) return null;

  const isRunning = task.status === "running";
  const isCompleted = task.status === "completed";
  const isFailed = task.status === "failed";
  const isWaiting = task.status === "waiting";

  const output = task.output as
    | {
        annualPeriodCount?: number;
        monthlyPeriodCount?: number;
        quarterlyPeriodCount?: number;
        weeklyPeriodCount?: number;
        totalIndicatorsMet?: number;
        totalIndicatorsAssessed?: number;
        estimatedInsolvencyDate?: string | null;
      }
    | undefined;

  const totalPeriods =
    (output?.annualPeriodCount ?? 0) +
    (output?.monthlyPeriodCount ?? 0) +
    (output?.quarterlyPeriodCount ?? 0) +
    (output?.weeklyPeriodCount ?? 0);

  const handleViewDocument = (docId: string) => {
    setViewingDocumentId(docId as Id<"documents">);
  };

  const tabs = tabsForSection(section);
  const siblingSection = section === "historical" ? "solvency" : "historical";
  const title = titleForSection(section);
  // Going to Solvency keeps the chosen granularity and any assessment the
  // practitioner came from; going to Historical keeps the granularity only.
  const siblingContext =
    siblingSection === "solvency"
      ? { granularity: context.granularity, indicator: context.indicator }
      : { granularity: context.granularity };
  const evidenceIndicatorName =
    section === "historical" && context.indicator
      ? (indicators.find((i) => i.indicatorKey === context.indicator)
          ?.indicatorName ?? null)
      : null;

  return (
    <Tabs
      defaultValue={defaultTabForSection(section, isRunning)}
      className="-m-6 flex flex-1 flex-col bg-muted px-6 pb-6 [overflow-anchor:none]"
    >
      {/* Sticky header + tabs */}
      <div className="sticky top-16 z-[9] -mx-6 bg-muted px-6 pb-2 pt-6">
        <div className="flex flex-wrap items-center gap-3 pb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={onBack}
            className="gap-1.5"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
          <div className="flex min-w-0 flex-[1_1_20rem] flex-wrap items-center gap-2">
            <StatusDot status={task.status} />
            <h3 className="text-lg font-semibold">{title}</h3>
            {/* Cross-link to the sibling destination (MOR-1280) — the two
                read the same underlying financial evidence. */}
            {onOpenSection && (
              <Button
                variant="link"
                size="sm"
                onClick={() => onOpenSection(siblingSection, siblingContext)}
                className="h-auto gap-1 px-1 text-xs text-muted-foreground"
              >
                {siblingSection === "solvency"
                  ? "View solvency indicators"
                  : `View ${FINANCIAL_SECTION_TITLES.historical.toLowerCase()}`}
                <ArrowRight className="h-3 w-3" />
              </Button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {task.duration != null && (
              <Badge variant="outline" className="text-xs tabular-nums">
                {formatDuration(task.duration)}
              </Badge>
            )}
            {isCompleted && task.output && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setChatOpen(true)}
                className="gap-1.5"
              >
                <MessageSquare className="h-3.5 w-3.5" /> Chat
              </Button>
            )}
            {isCompleted && task.output && (
              <ExportButton
                content={buildFinancialAnalysisExport(
                  periodEntries ?? [],
                  indicators,
                  task.output,
                  exportBasis,
                )}
                filename={
                  section === "historical"
                    ? "Historical_Financial_Analysis"
                    : "Financial_Analysis_Summary"
                }
              />
            )}
            {(isRunning || isCompleted || isFailed) && (
              <Button
                variant="outline"
                size="sm"
                onClick={onRerun}
                disabled={isViewer}
                className="gap-1.5"
              >
                <RotateCcw className="h-3.5 w-3.5" /> Re-run
              </Button>
            )}
            {!isViewer && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleReextract}
                disabled={reextract.isPending}
                className="gap-1.5"
              >
                <RefreshCw
                  className={`h-3.5 w-3.5 ${reextract.isPending ? "animate-spin" : ""}`}
                />
                {reextract.isPending
                  ? "Re-extracting…"
                  : "Re-extract financials"}
              </Button>
            )}
          </div>
        </div>

        {!isWaiting && (
          <TabsList className="w-full">
            {tabs.includes("worksheets") && (
              <TabsTrigger value="worksheets" className="gap-1.5">
                <Table2 className="h-3.5 w-3.5" />
                Worksheets
                {totalPeriods > 0 && (
                  <Badge
                    variant="secondary"
                    className="ml-0.5 px-1 text-[10px]"
                  >
                    {totalPeriods}
                  </Badge>
                )}
              </TabsTrigger>
            )}
            {tabs.includes("indicators") && (
              <TabsTrigger value="indicators" className="gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5" />
                Indicators
                {output?.totalIndicatorsAssessed != null &&
                  output.totalIndicatorsAssessed > 0 && (
                    <Badge
                      variant="secondary"
                      className="ml-0.5 px-1 text-[10px]"
                    >
                      {output.totalIndicatorsMet ?? 0}/
                      {output.totalIndicatorsAssessed}
                    </Badge>
                  )}
              </TabsTrigger>
            )}
            <TabsTrigger value="feed" className="gap-1.5">
              <Bot className="h-3.5 w-3.5" />
              Agent
              {isRunning && (
                <span className="relative ml-1 flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                </span>
              )}
            </TabsTrigger>
          </TabsList>
        )}
      </div>

      {/* Waiting state */}
      {isWaiting && (
        <div className="flex flex-col items-center gap-3 py-16">
          <div className="rounded-full bg-muted/50 p-4">
            <Table2 className="h-8 w-8 text-muted-foreground/40" />
          </div>
          <div className="text-center">
            <p className="text-sm font-medium">Ready to analyse</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Extracts period-indexed financial data from P&L, Balance Sheet,
              Trial Balance, and GL records.
            </p>
          </div>
        </div>
      )}

      {/* Active / completed — the section's tabs */}
      {!isWaiting && (
        <>
          {tabs.includes("worksheets") && (
            <TabsContent value="worksheets" className="mt-2">
              <FinancialWorksheetPanel
                jobId={jobId}
                taskId={taskId}
                isRunning={isRunning}
                onViewDocument={handleViewDocument}
                isViewer={isViewer}
                preferredGranularity={context.granularity}
                onGranularityChange={
                  onContextChange
                    ? (granularity) => onContextChange({ granularity })
                    : undefined
                }
                evidenceFocus={
                  context.period || evidenceIndicatorName
                    ? {
                        periodLabel: context.period,
                        indicatorName: evidenceIndicatorName ?? undefined,
                        onReturn:
                          context.indicator && onOpenSection
                            ? () =>
                                onOpenSection("solvency", {
                                  granularity: context.granularity,
                                  indicator: context.indicator,
                                })
                            : undefined,
                        onDismiss: onContextChange
                          ? () =>
                              onContextChange({
                                period: undefined,
                                indicator: undefined,
                              })
                          : undefined,
                      }
                    : undefined
                }
              />
            </TabsContent>
          )}

          {tabs.includes("indicators") && (
            <TabsContent value="indicators" className="mt-2">
              <FinancialIndicatorsPanel
                jobId={jobId}
                output={task.output}
                isRunning={isRunning}
                onViewDocument={handleViewDocument}
                isViewer={isViewer}
                selectedIndicatorKey={context.indicator}
                onSelectedIndicatorChange={
                  onContextChange
                    ? (indicator) => onContextChange({ indicator })
                    : undefined
                }
                onViewSupportingFinancials={
                  onOpenSection
                    ? ({ indicatorKey, period }) =>
                        onOpenSection("historical", {
                          indicator: indicatorKey,
                          period,
                        })
                    : undefined
                }
              />
            </TabsContent>
          )}

          <TabsContent value="feed" className="mt-2">
            <UnifiedFeed
              task={task}
              subAgents={[]}
              taskTree={getTaskTree(task.workingMemory)}
              isRunning={isRunning}
            />
          </TabsContent>
        </>
      )}

      <SummaryChatSheet
        open={chatOpen}
        onOpenChange={setChatOpen}
        taskId={taskId}
        jobId={jobId}
        agentName="Solvency Analysis"
      />

      {/* Document detail sheet */}
      <DocumentDetail
        documentId={viewingDocumentId}
        isViewer
        open={viewingDocumentId !== null}
        onClose={() => setViewingDocumentId(null)}
      />
      <AiDisclaimer className="mt-4 text-center" />
    </Tabs>
  );
}
